import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  type ButtonLevel,
  type ButtonPalette,
  type ButtonSize,
  type TypographySize,
  Button,
  Card,
  LoadingSpinner,
  TextInput,
  Typography,
  useNotification,
} from '@eucalyptusvc/design-system';
import type {
  ProfileWithDvaQuery,
  UpsertDvaFileNumberMutation,
  UpsertDvaFileNumberMutationVariables,
} from '@customer-frontend/graphql-types';

const GET_PROFILE_DVA_QUERY = gql`
  query ProfileWithDva {
    profile {
      id
      dvaFileNumber
    }
  }
`;

const UPSERT_DVA_MUTATION = gql`
  mutation UpsertDvaFileNumber($input: UpsertDvaFileNumberInput!) {
    upsertDvaFileNumber(input: $input) {
      user {
        id
        dvaFileNumber
      }
    }
  }
`;

interface FormValues {
  dvaFileNumber: string;
}

type DvaFileNumberDetailsProps = {
  styleConfig: {
    format: 'expandableCard' | 'card';
    paragraphSize: TypographySize;
    cancelButton?: {
      level?: ButtonLevel;
      palette?: ButtonPalette;
    };
    editButton?: {
      level?: ButtonLevel;
      palette?: ButtonPalette;
      size?: ButtonSize;
    };
    saveButton?: {
      palette?: ButtonPalette;
    };
  };
};

export const DvaFileNumberDetails = ({
  styleConfig,
}: DvaFileNumberDetailsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { formatMessage } = useIntl();
  const notify = useNotification();

  const { data, loading: loadingProfile } = useQuery<ProfileWithDvaQuery>(
    GET_PROFILE_DVA_QUERY,
  );

  const { handleSubmit, register } = useForm<FormValues>({
    defaultValues: {
      dvaFileNumber: data?.profile?.dvaFileNumber || '',
    },
  });

  const [upsertDva, { loading: upsertingDva }] = useMutation<
    UpsertDvaFileNumberMutation,
    UpsertDvaFileNumberMutationVariables
  >(UPSERT_DVA_MUTATION, {
    onCompleted: () => {
      setIsEditing(false);
      notify.success({
        message: formatMessage({
          defaultMessage: 'Successfully updated your DVA number.',
        }),
      });
    },
    onError: () => {
      notify.error({
        message: formatMessage({
          defaultMessage: 'Failed to update your DVA number.',
        }),
      });
    },
  });

  const onSubmit = (formData: FormValues) => {
    if (!data?.profile?.id) {
      notify.error({
        message: formatMessage({
          defaultMessage: 'Failed to update your DVA number.',
        }),
      });
      return;
    }

    upsertDva({
      variables: {
        input: {
          customerId: data.profile.id,
          dvaFileNumber: formData.dvaFileNumber,
        },
      },
    });
  };

  if (loadingProfile) {
    return (
      <div className="flex justify-center p-5">
        <LoadingSpinner />
      </div>
    );
  }

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (styleConfig.format === 'card') {
      return <Card>{children}</Card>;
    }
    return <>{children}</>;
  };

  if (isEditing) {
    return (
      <Wrapper>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {styleConfig.format === 'card' && (
            <Typography isBold size="md">
              <FormattedMessage defaultMessage="DVA Details" />
            </Typography>
          )}
          <TextInput
            label={formatMessage({
              defaultMessage: 'DVA Number',
            })}
            name="dvaFileNumber"
            ref={register}
            autoFocus
          />
          <div className="flex gap-4 justify-end">
            <Button
              isFullWidth
              level={styleConfig?.cancelButton?.level}
              palette={styleConfig?.cancelButton?.palette}
              onClick={() => setIsEditing(false)}
              aria-label={formatMessage({
                defaultMessage: 'Cancel',
                description: 'Cancel editing DVA number',
              })}
            >
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
            <Button
              isFullWidth
              isSubmit
              palette={styleConfig?.saveButton?.palette}
              isLoading={upsertingDva}
              aria-label={formatMessage({
                defaultMessage: 'Save',
                description: 'Update DVA number',
              })}
            >
              <FormattedMessage defaultMessage="Save" />
            </Button>
          </div>
        </form>
      </Wrapper>
    );
  }

  if (styleConfig.format === 'expandableCard') {
    return (
      <Wrapper>
        <div className="flex items-start justify-between gap-4">
          {data?.profile?.dvaFileNumber ? (
            <Typography size={styleConfig.paragraphSize ?? 'paragraph'}>
              <FormattedMessage defaultMessage="DVA Number: ••••••••" />
            </Typography>
          ) : (
            <Typography size={styleConfig.paragraphSize ?? 'paragraph'}>
              <FormattedMessage defaultMessage="Add your DVA (Department of Veteran Affairs) details for your patient profile" />
            </Typography>
          )}
          <Button
            level={styleConfig?.editButton?.level}
            palette={styleConfig?.editButton?.palette}
            size={styleConfig?.editButton?.size}
            onClick={() => {
              setIsEditing(true);
            }}
            aria-label={formatMessage({
              defaultMessage: 'Toggle editing of DVA number',
            })}
          >
            {data?.profile?.dvaFileNumber ? (
              <FormattedMessage defaultMessage="Edit" />
            ) : (
              <FormattedMessage defaultMessage="Add" />
            )}
          </Button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="flex items-start justify-between">
        <Typography isBold size="md">
          <FormattedMessage defaultMessage="DVA Details" />
        </Typography>
        <Button
          level={styleConfig?.editButton?.level}
          palette={styleConfig?.editButton?.palette}
          size={styleConfig?.editButton?.size}
          onClick={() => {
            setIsEditing(true);
          }}
          aria-label={formatMessage({
            defaultMessage: 'Toggle editing of DVA number',
          })}
        >
          {data?.profile?.dvaFileNumber ? 'Edit' : 'Add'}
        </Button>
      </div>

      <div className="mt-2">
        {data?.profile?.dvaFileNumber ? (
          <Typography size={styleConfig?.paragraphSize}>
            <FormattedMessage defaultMessage="DVA Number: ••••••••" />
          </Typography>
        ) : (
          <Typography size={styleConfig?.paragraphSize}>
            <FormattedMessage defaultMessage="Add your DVA (Department of Veteran Affairs) details for your patient profile" />
          </Typography>
        )}
      </div>
    </Wrapper>
  );
};
